import { useEffect, useRef, useState } from "react";
import { findMarketplaceCategory, MARKETPLACE_CATEGORIES } from "@puntopy/contracts";

const categories = [{ id: "all", label: "Todo", emoji: "✨", value: "" }, ...MARKETPLACE_CATEGORIES.map((category) => ({ ...category, value: category.label }))];

function revealCategory(rail: HTMLDivElement | null, button: HTMLElement | null | undefined) {
  if (!rail || !button) return;
  const container = rail.getBoundingClientRect();
  const item = button.getBoundingClientRect();
  if (item.left < container.left) rail.scrollLeft -= container.left - item.left + 3;
  else if (item.right > container.right) rail.scrollLeft += item.right - container.right + 3;
}

export default function CategoryRail({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const rail = useRef<HTMLDivElement>(null);
  const [canPrevious, setCanPrevious] = useState(false);
  const [canNext, setCanNext] = useState(true);
  const [focused, setFocused] = useState(0);
  const selectedId = value ? findMarketplaceCategory(value)?.id : "all";
  useEffect(() => {
    const element = rail.current;
    if (!element) return;
    const update = () => { setCanPrevious(element.scrollLeft > 4); setCanNext(element.scrollLeft + element.clientWidth < element.scrollWidth - 4); };
    const observer = new ResizeObserver(update);
    observer.observe(element); element.addEventListener("scroll", update, { passive: true }); update();
    return () => { observer.disconnect(); element.removeEventListener("scroll", update); };
  }, []);
  useEffect(() => {
    const element = rail.current?.querySelector<HTMLElement>("[aria-pressed='true']");
    revealCategory(rail.current, element);
    const index = categories.findIndex((category) => category.id === selectedId);
    if (index >= 0) setFocused(index);
  }, [selectedId]);
  const move = (direction: number) => rail.current?.scrollBy({ left: direction * Math.max(260, rail.current.clientWidth * 0.75), behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth" });
  return <nav className="pp-category-section" aria-label="Explorar por categoría">
    <div className="pp-category-heading"><span>Encontrá por categoría</span><small>Deslizá para descubrir más</small><div className="pp-category-controls">
      <button type="button" aria-label="Categorías anteriores" disabled={!canPrevious} onClick={() => move(-1)}>‹</button>
      <button type="button" aria-label="Más categorías" disabled={!canNext} onClick={() => move(1)}>›</button>
    </div></div>
    <div className="mp-category-rail" ref={rail} onKeyDown={(event) => {
      const keys = ["ArrowLeft", "ArrowRight", "Home", "End"];
      if (!keys.includes(event.key)) return;
      event.preventDefault();
      const next = event.key === "Home" ? 0 : event.key === "End" ? categories.length - 1 : Math.max(0, Math.min(categories.length - 1, focused + (event.key === "ArrowRight" ? 1 : -1)));
      const button = rail.current?.querySelectorAll<HTMLButtonElement>("button")[next];
      button?.focus({ preventScroll: true }); revealCategory(rail.current, button); setFocused(next);
    }}>
      {categories.map((category, index) => <button type="button" key={category.id} tabIndex={focused === index ? 0 : -1} onFocus={() => setFocused(index)}
        className={selectedId === category.id ? "selected" : ""} aria-pressed={selectedId === category.id} onClick={() => onChange(category.value)}>
        <span className="pp-category-emoji" aria-hidden="true">{category.emoji}</span>{category.label}
      </button>)}
    </div>
  </nav>;
}
